import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { FaCheckCircle, FaTimesCircle } from "react-icons/fa";

import Header from "../../components/Header/Header";
import Footer from "../../components/Footer/Footer";
import Loader from "../../components/Loader/Loader";
import { confirmCodOrder, getCodConfirmation } from "../../api/checkoutApi";
import { formatDeliveryWindow } from "../../utils/checkoutTotals";
import "../OrderConfirmation/OrderConfirmation.css";
import "./ConfirmOrder.css";

const money = (n) => `₹${Number(n).toLocaleString("en-IN")}`;

// The page behind the "Confirm your order" link we send to a Cash on Delivery customer (email, WhatsApp, SMS).
// One tap confirms the order so it can be dispatched. The link itself is the proof it is really them.
export default function ConfirmOrder() {
  const { token } = useParams();
  const [order, setOrder] = useState(null);
  const [state, setState] = useState("loading"); // loading | ready | invalid
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let live = true;
    getCodConfirmation(token).then((result) => {
      if (!live) return;
      if (result.ok && result.success) {
        setOrder(result.data);
        setState("ready");
      } else {
        setError(result.message || "This confirmation link is not valid.");
        setState("invalid");
      }
    });
    return () => {
      live = false;
    };
  }, [token]);

  const confirm = async () => {
    setConfirming(true);
    setError("");
    const result = await confirmCodOrder(token);
    setConfirming(false);
    if (result.ok && result.success) setOrder(result.data);
    else setError(result.message || "We could not confirm your order. Please try again.");
  };

  const deliveryBy = order ? formatDeliveryWindow(order.estimatedDeliveryFrom, order.estimatedDeliveryTo) : "";

  return (
    <>
      <Header />
      <div className="confirmation-page">
        <div className="confirmation-card confirm-order-card">
          {state === "loading" && <Loader label="Loading your order..." />}

          {state === "invalid" && (
            <>
              <FaTimesCircle className="confirmation-icon failed" />
              <h1>Link not valid</h1>
              <p className="confirmation-message">{error}</p>
              <div className="confirmation-actions">
                <Link to="/contact" className="confirmation-btn primary">
                  Contact us
                </Link>
              </div>
            </>
          )}

          {state === "ready" && order && (
            <>
              {order.confirmed ? <FaCheckCircle className="confirmation-icon success" /> : null}
              <h1>
                {order.cancelled ? "Order cancelled" : order.confirmed ? "Thank you, your order is confirmed" : `Confirm your order, ${order.customerName || "there"}`}
              </h1>
              <p className="confirmation-message">
                {order.cancelled
                  ? `Order #${order.orderNumber} was cancelled, so it cannot be confirmed.`
                  : order.confirmed
                  ? "We will pack and dispatch it now, and keep you updated on every step."
                  : "Please check the details below and tap Confirm, so we can dispatch your Cash on Delivery order."}
              </p>

              <div className="confirmation-order-details">
                <div>
                  <span>Order Number</span>
                  <strong>#{order.orderNumber}</strong>
                </div>
                {deliveryBy && (
                  <div>
                    <span>Delivery</span>
                    <strong>{deliveryBy.replace("Delivery by ", "By ")}</strong>
                  </div>
                )}
                {order.deliverTo && (
                  <div>
                    <span>Deliver to</span>
                    <strong>{order.deliverTo}</strong>
                  </div>
                )}
                <div>
                  <span>Total</span>
                  <strong>{money(order.total)}</strong>
                </div>
                {order.advanceAmount > 0 && (
                  <div>
                    <span>Paid online already</span>
                    <strong>{money(order.advanceAmount)}</strong>
                  </div>
                )}
                <div>
                  <span>Pay in cash on delivery</span>
                  <strong>{money(order.payOnDelivery)}</strong>
                </div>
              </div>

              <ul className="confirm-order-items">
                {order.items.map((item) => (
                  <li key={item.name}>
                    {item.name} × {item.quantity} <strong>{money(item.lineTotal)}</strong>
                  </li>
                ))}
              </ul>

              {error && (
                <p className="confirm-order-error" role="alert">
                  {error}
                </p>
              )}

              {!order.confirmed && !order.cancelled && (
                <div className="confirmation-actions">
                  <button type="button" className="confirmation-btn primary" onClick={confirm} disabled={confirming}>
                    {confirming ? "Confirming..." : "Confirm my order"}
                  </button>
                </div>
              )}
              <p className="confirmation-message confirm-order-foot">
                Did not place this order? Ignore this page, or contact us and we will cancel it.
              </p>
            </>
          )}
        </div>
      </div>
      <Footer />
    </>
  );
}
